import { LiteratureReviewPlan } from "@core/domain/models/literature-review-plan.model";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";

const formatName = (name: string): string => {
  const cleanName = name.replace(/[^a-zA-Z0-9]+/g, " ").trim();
  return `"${cleanName}"`;
};

const formatNamesList = (names: string[]): string[] =>
  names.filter((name) => name.trim()).map((name) => formatName(name));

const buildQueryPart = (names: string[]): string => {
  if (names.length === 0) return "";
  const separator = " OR ";
  const query = names.join(separator);
  return names.length > 1 ? `(${query})` : query;
};

const buildMdidQueryPart = (names: string[]): string => {
  if (names.length === 0) return "";
  const separator = " AND ";
  const query = names.join(separator);
  return names.length > 1 ? `(${query})` : query;
};
const combineQueries = ({
  deviceQuery,
  manufacturerQuery,
  separator = " AND ",
}: {
  deviceQuery: string;
  manufacturerQuery: string;
  separator: string;
}): string => {
  if (!deviceQuery) return "";

  return manufacturerQuery ? `${deviceQuery}${separator}${manufacturerQuery}` : deviceQuery;
};

export function generatePmsSearchQuery({
  importSourceId,
  plan,
}: {
  importSourceId?: string;
  plan: LiteratureReviewPlan;
}): string {
  const deviceNames = formatNamesList([
    plan.deviceName ?? "",
    ...(plan.alternativeDeviceNames ?? []),
  ]);

  const manufacturerNames = formatNamesList([
    plan.manufacturerName ?? "",
    ...(plan.alternativeManufacturerNames ?? []),
  ]);

  if (importSourceId === BuiltInImportSourceId.MDID) {
    const deviceQuery = buildMdidQueryPart(deviceNames);
    const manufacturerQuery = buildMdidQueryPart(manufacturerNames);
    return combineQueries({
      deviceQuery,
      manufacturerQuery,
      separator: " AND ",
    });
  } else if (importSourceId === BuiltInImportSourceId.GOOGLE_SCHOLAR) {
    const deviceQuery = buildQueryPart(deviceNames);
    const manufacturerQuery = buildQueryPart(manufacturerNames);
    return combineQueries({ deviceQuery, manufacturerQuery, separator: " " });
  }

  const deviceQuery = buildQueryPart(deviceNames);
  const manufacturerQuery = buildQueryPart(manufacturerNames);
  return combineQueries({ deviceQuery, manufacturerQuery, separator: " AND " });
}
